export type AppNotification = {
  id: string
  title: string
  message: string
  bulk_session_id?: string | null
  generation_job_id?: string | null
  subject_title?: string | null
  topic?: string | null
  chapter?: string | null
  question_count?: number | null
  uploaded_at?: string | null
  created_at?: string
  is_read?: boolean
}

/** Backend datetimes are UTC but often serialized without a timezone suffix. */
export const parseServerDateTime = (isoDate?: string | null): Date | null => {
  if (!isoDate) return null

  const trimmed = isoDate.trim()
  if (!trimmed) return null

  const hasTimezone = /[zZ]$|[+-]\d{2}:\d{2}$/.test(trimmed)
  const normalized = hasTimezone ? trimmed : `${trimmed}Z`
  const date = new Date(normalized)

  return Number.isNaN(date.getTime()) ? null : date
}

const getNotificationTimestamp = (notification: AppNotification) =>
  parseServerDateTime(notification.created_at) ??
  parseServerDateTime(notification.uploaded_at)

export const sortNotificationsByNewest = (items: AppNotification[]) =>
  [...items].sort((left, right) => {
    const leftTime = getNotificationTimestamp(left)?.getTime() ?? 0
    const rightTime = getNotificationTimestamp(right)?.getTime() ?? 0
    return rightTime - leftTime
  })

export const getNotificationContextLine = (notification: AppNotification) => {
  const parts = [
    notification.subject_title,
    notification.topic,
    notification.chapter,
  ].filter(Boolean)

  if (parts.length > 0) return parts.join(" · ")

  const legacyMessage = notification.message?.trim()
  if (legacyMessage && !legacyMessage.includes("session (ID:")) {
    return legacyMessage.split("—")[0]?.trim() || legacyMessage
  }

  return "Bulk upload"
}

export const getNotificationMetaLine = (notification: AppNotification) => {
  const count = notification.question_count
  const countLabel =
    count === 1
      ? "1 question ready"
      : count && count > 0
        ? `${count} questions ready`
        : "Ready for preview"

  const timeSource = notification.uploaded_at || notification.created_at
  const timeLabel = timeSource ? formatRelativeTime(timeSource) : null

  return timeLabel ? `${countLabel} · ${timeLabel}` : countLabel
}

export const formatRelativeTime = (isoDate: string) => {
  const date = parseServerDateTime(isoDate)
  if (!date) return null

  const diffMs = Date.now() - date.getTime()
  const diffMinutes = Math.floor(diffMs / 60000)

  if (diffMinutes < 1) return "Just now"
  if (diffMinutes < 60) {
    return diffMinutes === 1 ? "1 minute ago" : `${diffMinutes} minutes ago`
  }

  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) {
    return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`
  }

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) {
    return diffDays === 1 ? "Yesterday" : `${diffDays} days ago`
  }

  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
